import React, { useState, useEffect } from "react";
import axios from "axios";

import CharacterList from "../components/CharacterList";
import CharacterFilter from "../components/CharacterFilter";
import Pagination from "../components/Pagination";
import CardSkeletonGrid from "../components/Skeleton";
import { handleError } from "../utils/errorHandler";
import { API_BASE } from "../utils/rickAndMorty";
import { Character } from "../types";

const SEARCH_DEBOUNCE_MS = 350;

const CharactersPage: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // What the user typed, and what has actually been sent to the API.
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Typing used to fire one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchCharacters = async () => {
      setIsLoading(true);
      const params = new URLSearchParams({ page: String(currentPage) });
      if (searchQuery) params.set("name", searchQuery);
      if (statusFilter) params.set("status", statusFilter);
      if (genderFilter) params.set("gender", genderFilter);

      try {
        const { data } = await axios.get(`${API_BASE}/character?${params}`);
        if (cancelled) return;
        setCharacters(data.results);
        setTotalPages(data.info.pages);
        setTotalCount(data.info.count);
        setError(null);
      } catch (requestError) {
        if (cancelled) return;
        // The API answers "nothing matched" with 404 — that is an empty
        // result, not a failure, and it must not look like a broken page.
        if (axios.isAxiosError(requestError) && requestError.response?.status === 404) {
          setCharacters([]);
          setTotalPages(0);
          setTotalCount(0);
          setError(null);
        } else {
          handleError(requestError);
          setError("Could not load characters. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCharacters();
    return () => {
      cancelled = true;
    };
  }, [currentPage, searchQuery, statusFilter, genderFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setSearchTerm("");
    setStatusFilter("");
    setGenderFilter("");
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-white">Characters</h1>
        <p className="mt-1 text-sm text-slate-400">
          {isLoading
            ? "Searching…"
            : `${totalCount} character${totalCount === 1 ? "" : "s"} found`}
        </p>
      </header>

      <CharacterFilter
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        genderFilter={genderFilter}
        onSearchChange={setSearchTerm}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        onGenderChange={(value) => {
          setGenderFilter(value);
          setCurrentPage(1);
        }}
        onReset={handleReset}
      />

      {isLoading ? (
        <CardSkeletonGrid count={8} />
      ) : error ? (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300">
          {error}
        </p>
      ) : characters.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 p-12 text-center">
          <p className="font-medium text-white">No characters match</p>
          <p className="mt-1 text-sm text-slate-400">
            Try a different name, or clear the filters.
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-6 rounded-lg bg-portal-400 px-4 py-2 text-sm font-medium text-space-950 transition hover:bg-portal-200"
          >
            Reset filters
          </button>
        </div>
      ) : (
        <>
          <CharacterList characters={characters} />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default CharactersPage;
